import React from "react";
import { observer } from "mobx-react";
import { observable, computed } from "mobx";
import { SnapshotOrInstance } from "mobx-state-tree";
import { generatePath, useNavigate } from "react-router-dom";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { es, enUS } from "date-fns/locale";

import CalendarStore from "stores/CalendarStore";

import I32Union from "assets/icons/I32Union";
import { Placeholders } from "assets/images/placeholders";

import { Page } from "components/Layout";
import DefaultCalendar from "components/Calendar";
import Spinner from "components/Spinner";
import { List } from "components/Collection";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import Placeholder from "components/Placeholder";

import Summary from "./Summary";
import AddWorkoutForm from "./AddWorkoutForm";
import NavList from "./NavList";

import { NoInternetConnectionError } from "services/api";

import { getMarkedDates } from "./utils";

import ROUTES from "navigation/routes";

import { Workout } from "models/Workout";

import styleConst from "constants/style";
import {
  Container,
  Row,
  SubmitButton,
  Title,
  Column,
  CalendarContainer,
  LoaderContainer,
  SummaryContainer,
  SelectedDayTitle,
  PlaceholderWrapper,
} from "./styles";
import { WithTranslation, withTranslation } from "react-i18next";
import { convertToUTCZero } from "utils/timezones";

type Props = GlobalPopupProps & WithTranslation;

type Collection = {
  titleKey: string;
  items: SnapshotOrInstance<typeof Workout>[];
};

@observer
class Calendar extends React.Component<Props> {
  static contextType = React.createContext({ navigate: () => {} });
  @observable date: Date = new Date();
  @observable selectedDay: Date = new Date();
  @observable collections: Collection[] = [];
  @observable isNoInternetConnectionError: boolean = false;
  @observable isLoading: boolean = false;

  get placeholder() {
    const t: (key: string) => string = this.props.t;
    return (
      <PlaceholderWrapper>
        <Placeholder
          imageUrl={Placeholders.Workout}
          text={t("messages.no-workouts")}
          inline={false}
        />
      </PlaceholderWrapper>
    );
  }

  @computed get streak(): number {
    return CalendarStore.workoutStreaks?.streakDays || 0;
  }

  @computed get scheduled() {
    return CalendarStore.mappedScheduled;
  }

  @computed get completed() {
    return CalendarStore.mappedCompleted;
  }

  @computed get selectedDayTitle() {
    const lang = this.props.i18n?.language;
    const currentLocale = lang?.startsWith("es") ? es : enUS;
    if (!this.selectedDay) return "";
    let formatted = format(this.selectedDay, "EEEE, LLLL d", {
      locale: currentLocale,
    });
    // Capitalize the first letter of the day and month
    formatted = formatted.replace(
      /^(\w)([^,]*), (\w)([^ ]*)/,
      (match, d1, dRest, m1, mRest) => {
        return d1.toUpperCase() + dRest + ", " + m1.toUpperCase() + mRest;
      },
    );
    return formatted;
  }

  async componentDidMount() {
    this.isLoading = true;
    await this.fetch();
    this.onDayPress(this.selectedDay);
    this.isLoading = false;
  }

  fetch = async () => {
    try {
      const startDate = startOfMonth(this.date);
      const endDate = endOfMonth(this.date);
      await CalendarStore.fetch(
        convertToUTCZero(startDate),
        convertToUTCZero(endDate),
      );
      CalendarStore.fetchWorkoutStreak();
    } catch (error) {
      if (error instanceof NoInternetConnectionError) {
        this.isNoInternetConnectionError = true;
      }
    }
  };

  onDayPress = (day: Date) => {
    this.selectedDay = day;
    this.collections = CalendarStore.getCollectionsByDay(this.selectedDay);
  };

  onChangeMonth = (date: Date) => {
    this.date = date;
    this.fetch();
  };

  onAddWorkoutPress = () => {
    const { openPopup } = this.props;

    openPopup({
      key: "AddWorkoutForm",
      exitButtonColor: styleConst.colors.black25,
      exitButtonZIndex: 10000,
      render: ({ close }) => (
        <AddWorkoutForm
          onSubmit={async date => {
            await this.fetch();
            this.onDayPress(date);
            close();
          }}
          onClose={close}
        />
      ),
    });
  };

  onWorkoutNavigate = (workout: any) => {
    const navigate =
      (this.props as any).navigate ||
      ((path: string) => {
        window.location.href = path;
      });
    const { id: workoutId, date: scheduleDate } = workout;

    navigate(
      generatePath(ROUTES.CALENDAR_WORKOUT_SCHEDULE_DATE_DETAILS, {
        workoutId,
        scheduleDate,
      }),
    );
  };

  render() {
    const t: (key: string) => string = this.props.t;
    const isSpanish = this.props.i18n?.language?.startsWith("es");
    const submitButtonWidth = isSpanish ? 240 : 200;
    // Defining locale for calendar
    const calendarLocale = isSpanish ? "es-ES" : "en-US";
    return (
      <Page
        isNoInternetConnectionError={this.isNoInternetConnectionError}
        isLoading={this.isLoading}
        topNavigation={<NavList />}
      >
        <Container>
          <Row>
            <Title>{t("titles.calendar")}</Title>
            <SubmitButton
              text={t("actions.add-workout")}
              onClick={this.onAddWorkoutPress}
              loading={this.isLoading}
              fontSize={16}
              fontFamily={styleConst.fonts.roboto}
              fontWeight={700}
              iconLeft={<I32Union />}
              width={submitButtonWidth}
            />
          </Row>
          <Row>
            <Column>
              <CalendarContainer>
                <DefaultCalendar
                  markedDates={getMarkedDates({
                    scheduled: this.scheduled,
                    completed: this.completed,
                  })}
                  value={this.selectedDay}
                  onClickDay={this.onDayPress}
                  headerFontSize={26}
                  onChangeMonth={this.onChangeMonth}
                  locale={calendarLocale}
                />
                {CalendarStore.isLoading ? (
                  <LoaderContainer>
                    <Spinner color={styleConst.colors.primary} large />
                  </LoaderContainer>
                ) : null}
              </CalendarContainer>
              {!CalendarStore.isLoading && !CalendarStore.workoutStreaksLoading && (
                <SummaryContainer>
                  <Summary
                    scheduled={this.scheduled.length}
                    completed={this.completed.length}
                    streak={this.streak}
                  />
                </SummaryContainer>
              )}
            </Column>
            <Column>
              <SelectedDayTitle>{this.selectedDayTitle}</SelectedDayTitle>
              {this.collections.length
                ? this.collections.map(({ titleKey, items }) => (
                    <List
                      key={titleKey}
                      title={t(titleKey)}
                      items={items}
                      onCardClick={this.onWorkoutNavigate}
                    />
                  ))
                : this.placeholder}
            </Column>
          </Row>
        </Container>
      </Page>
    );
  }
}

const CalendarWithHooks = (props: any) => {
  const navigate = useNavigate();
  return <Calendar {...props} navigate={navigate} />;
};

export default withTranslation()(withGlobalPopup(CalendarWithHooks));
